import { BaseRestDataProvider } from './BaseRestDataProvider';
import { BankTransaction } from '../models/BankTransaction';
import { toast } from 'react-toastify';

export interface Mt940ImportResult {
  imported: number;
  skipped: number;
  transactions: BankTransaction[];
}

export interface BookTransactionLine {
  ledgerId: string;
  description: string;
  amount: number;
}

export interface BookTransactionRequest {
  fiscalYearId: string;
  lines: BookTransactionLine[];
  /** When true, stamps IsVatPaymentBooked on the transaction. */
  isVatPayment?: boolean;
}

export interface BookTransactionResult {
  referenceNumber: string;
}

export class BankTransactionRestDataProvider extends BaseRestDataProvider {
  constructor() {
    super('/v1/bank-transactions');
  }

  getAll(): Promise<BankTransaction[]> {
    return this.get<BankTransaction[]>(``);
  }

  getById(id: string): Promise<BankTransaction> {
    return this.get<BankTransaction>(`/${id}`);
  }

  getByAccount(accountIdentification: string): Promise<BankTransaction[]> {
    return this.get<BankTransaction[]>(`/by-account/${encodeURIComponent(accountIdentification)}`);
  }

  getByStatement(statementReference: string): Promise<BankTransaction[]> {
    return this.get<BankTransaction[]>(`/by-statement/${encodeURIComponent(statementReference)}`);
  }

  add(transaction: BankTransaction): Promise<BankTransaction> {
    return this.post<BankTransaction>(``, transaction);
  }

  update(id: string, transaction: BankTransaction): Promise<void> {
    return this.put<void>(`/${id}`, transaction);
  }

  delete(id: string): Promise<void> {
    return super.delete(`/${id}`);
  }

  book(id: string, request: BookTransactionRequest): Promise<BookTransactionResult> {
    return this.post<BookTransactionResult>(`/${id}/book`, request);
  }

  bookSupplementary(id: string, request: BookTransactionRequest): Promise<BookTransactionResult> {
    return this.post<BookTransactionResult>(`/${id}/book-supplementary`, request);
  }

  unbook(id: string): Promise<void> {
    return this.postVoid(`/${id}/unbook`);
  }

  async importMt940(bankAccountId: string, file: File): Promise<Mt940ImportResult> {
    const token = BaseRestDataProvider.getToken();
    const formData = new FormData();
    formData.append('bankAccountId', bankAccountId);
    formData.append('file', file);

    const response = await fetch(`${this.baseUrl}/import`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    });

    if (!response.ok) {
      const errorText = await response.text();
      toast.error('Could not import MT-940 file');
      throw new Error(errorText);
    }

    const result: Mt940ImportResult = await response.json();
    const skippedMsg = result.skipped > 0 ? `, ${result.skipped} duplicate(s) skipped` : '';
    toast.success(`MT-940 imported — ${result.imported} transaction(s) added${skippedMsg}`);
    return result;
  }
}

export type { BankTransaction };

